import { useTranslation } from 'react-i18next';
import { cn, Input, Textarea } from '@kognitos/lattice';
import type { TextInputProps } from '../types';

/**
 * Text input component for text values
 *
 * There is no sensitive/password variant: SPy has no sensitive type, so an
 * automation's input specs can never declare one. Connection and trigger
 * config, which does deal in secrets, handles that itself.
 *
 * `multiline` renders an auto-growing textarea instead, for prose-shaped text
 * (an IDP extraction rule, a prompt). `field-sizing-content` keeps it one line
 * for a short value and grows it with a long one, capped before it scrolls, so
 * a rail full of params stays navigable.
 *
 * @param props - Component props
 * @returns JSX element containing the text input field
 */
export function TextInput({
  name,
  value,
  onChange,
  error,
  required,
  placeholder,
  disabled,
  multiline,
  fillHeight,
  autoFocus,
}: TextInputProps) {
  const { t } = useTranslation('automation-inputs');

  const shared = {
    id: name,
    name,
    value,
    placeholder: placeholder || t('placeholders.enter', { field: name }),
    required,
    'aria-invalid': !!error,
    disabled,
    autoFocus,
    'data-testid': `input-${name}`,
  };

  if (multiline) {
    return (
      <Textarea
        {...shared}
        rows={1}
        onChange={e => onChange(e.target.value)}
        className={cn(
          fillHeight
            ? 'h-full w-full resize-none'
            : 'min-h-8 max-h-64 w-full resize-y overflow-auto field-sizing-content',
          error && 'border-destructive focus-visible:ring-destructive',
        )}
      />
    );
  }

  return (
    <Input
      {...shared}
      type="text"
      onChange={e => onChange(e.target.value)}
      className={
        error ? 'border-destructive focus-visible:ring-destructive' : ''
      }
    />
  );
}
